import { observer } from 'mobx-react-lite';
import { calendarViewRegionName, currentTimeAriaLabel } from './CalendarSurface.locstring.json';
import { jumpToRegion } from 'owa-locstrings/lib/strings/jumptoregion.locstring.json';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import CalendarDayList, { CalendarDayListHandle } from './CalendarDayList';
import type CalendarSurfaceConfig from '../interfaces/CalendarSurfaceConfig';
import MonthSurface from './MonthSurface';
import { observableNow } from 'owa-observable-datetime';
import { formatWeekDayTime } from 'owa-datetime';
import type SurfaceStore from '../interfaces/SurfaceStore';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';

import styles from './CalendarSurface.scss';
import { PixelsPerMinuteRatioProvider } from '../utils/usePixelsPerMinuteRatio';

/**
 * Props for calendar surface
 */
export interface CalendarSurfaceProps {
    config: CalendarSurfaceConfig;
    store: SurfaceStore;
    isHeaderTabbable?: boolean;
    monthViewHeaderHeight?: number;
}

export interface CalendarSurfaceHandle {
    getPopupBounds(anchorElement: HTMLElement): ClientRect;
    focusSurfaceRoot(): void;
}

/**
 * Main calendar surface component with navigation.
 */
export default observer(
    function CalendarSurface(props: CalendarSurfaceProps, ref: React.Ref<CalendarSurfaceHandle>) {
        React.useImperativeHandle(
            ref,
            () => ({
                getPopupBounds(anchorElement: HTMLElement): ClientRect {
                    if (popupBoundsFunction.current) {
                        return popupBoundsFunction.current(anchorElement);
                    }
                    return null;
                },
                /**
                 * Sets focus to the surface root element
                 */
                focusSurfaceRoot() {
                    if (surfaceRef.current) {
                        surfaceRef.current.focus();
                    }
                },
            }),
            []
        );

        const surfaceRef = React.useRef<HTMLDivElement>();
        const popupBoundsFunction = React.useRef<(anchorElement: HTMLElement) => ClientRect>();
        const setSurfaceRef = (ref: HTMLDivElement) => {
            surfaceRef.current = ref;
        };
        const setPopupBoundsFunction = (ref: CalendarDayListHandle) => {
            popupBoundsFunction.current = ref?.getPopupBounds;
        };
        let { store, config, isHeaderTabbable, monthViewHeaderHeight } = props;

        let view =
            store.dateRangeType !== DateRangeType.Month ? (
                <PixelsPerMinuteRatioProvider value={store.weekViewPixelsPerMinuteRatio}>
                    <CalendarDayList
                        config={config.dayViewConfig}
                        store={store}
                        ref={setPopupBoundsFunction}
                        shouldFillContainer={true}
                    />
                </PixelsPerMinuteRatioProvider>
            ) : (
                <MonthSurface config={config.monthViewConfig} store={store} />
            );

        // Resize month view if container size changes.
        useResizeObserver(surfaceRef, () => {
            if (
                !monthViewHeaderHeight ||
                !surfaceRef.current.clientHeight ||
                store.dateRangeType !== DateRangeType.Month
            ) {
                return;
            }

            const monthSurfaceHeight = surfaceRef.current.clientHeight - monthViewHeaderHeight;

            if (store.monthHeight !== monthSurfaceHeight) {
                window.dispatchEvent(new Event('resize'));
            }
        });

        return (
            <div className={styles.mainContent}>
                <SkipLinkRegion
                    skipLinkName={format(loc(jumpToRegion), loc(calendarViewRegionName))}
                    role="main"
                    regionName={loc(calendarViewRegionName)}
                    ref={setSurfaceRef}
                    className={styles.surfaceContainer}
                    tabIndex={isHeaderTabbable ? 1 : -1}
                    aria-label={format(
                        loc(currentTimeAriaLabel),
                        formatWeekDayTime(observableNow())
                    )}>
                    {view}
                </SkipLinkRegion>
            </div>
        );
    },
    { forwardRef: true }
);
